/**
 * SVG Path Parser
 * Converts SVG path commands (M, L, C, Q, A, Z) to Canvas API calls
 */

interface PathCommand {
  type: string
  args: number[]
}

/**
 * Parse SVG path string into commands
 * Example: "M 0 0 L 100 0 L 100 100 Z" → [{type:'M', args:[0,0]}, ...]
 */
export function parseSVGPath(pathData: string): PathCommand[] {
  const commands: PathCommand[] = []

  // Remove extra whitespace and split by command letters
  const normalized = pathData.trim().replace(/\s+/g, ' ')

  // Rotated CAD corners can contain tiny values serialized as 1e-9. Keep the
  // exponent with its number so coordinates neither jump nor lose the next arg.
  const commandRegex = /([MLHVCSQTAZmlhvcsqtaz])([\d\s,.eE+-]*)/g
  const numberRegex = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g
  let match: RegExpExecArray | null

  while ((match = commandRegex.exec(normalized)) !== null) {
    const type = match[1]
    const argsStr = match[2].trim()

    if (!argsStr && type.toUpperCase() !== 'Z') continue

    // SVG also allows signed numbers without a separator (e.g. "1-2").
    const args = (argsStr.match(numberRegex) || []).map(Number)

    commands.push({ type, args })
  }

  return commands
}

/**
 * Execute parsed SVG path commands on Canvas context
 */
export function executePathOnCanvas(ctx: CanvasRenderingContext2D, commands: PathCommand[]): void {
  let currentX = 0
  let currentY = 0
  let startX = 0
  let startY = 0

  for (const cmd of commands) {
    const { type, args } = cmd

    switch (type.toUpperCase()) {
      case 'M': // Move to (absolute)
        if (type === 'M') {
          currentX = args[0]
          currentY = args[1]
        } else {
          currentX += args[0]
          currentY += args[1]
        }
        ctx.moveTo(currentX, currentY)
        startX = currentX
        startY = currentY
        break

      case 'L': // Line to (absolute)
        if (type === 'L') {
          currentX = args[0]
          currentY = args[1]
        } else {
          currentX += args[0]
          currentY += args[1]
        }
        ctx.lineTo(currentX, currentY)
        break

      case 'H': // Horizontal line
        if (type === 'H') {
          currentX = args[0]
        } else {
          currentX += args[0]
        }
        ctx.lineTo(currentX, currentY)
        break

      case 'V': // Vertical line
        if (type === 'V') {
          currentY = args[0]
        } else {
          currentY += args[0]
        }
        ctx.lineTo(currentX, currentY)
        break

      case 'C': // Cubic Bezier (absolute)
        if (type === 'C') {
          ctx.bezierCurveTo(args[0], args[1], args[2], args[3], args[4], args[5])
          currentX = args[4]
          currentY = args[5]
        } else {
          ctx.bezierCurveTo(
            currentX + args[0],
            currentY + args[1],
            currentX + args[2],
            currentY + args[3],
            currentX + args[4],
            currentY + args[5]
          )
          currentX += args[4]
          currentY += args[5]
        }
        break

      case 'Q': // Quadratic Bezier
        if (type === 'Q') {
          ctx.quadraticCurveTo(args[0], args[1], args[2], args[3])
          currentX = args[2]
          currentY = args[3]
        } else {
          ctx.quadraticCurveTo(currentX + args[0], currentY + args[1], currentX + args[2], currentY + args[3])
          currentX += args[2]
          currentY += args[3]
        }
        break

      case 'A': // Arc (simplified - doesn't handle all SVG arc cases)
        // For now, approximate with lineTo
        // Full implementation would need ellipse arc conversion
        if (type === 'A') {
          currentX = args[5]
          currentY = args[6]
        } else {
          currentX += args[5]
          currentY += args[6]
        }
        ctx.lineTo(currentX, currentY)
        break

      case 'Z': // Close path
        ctx.closePath()
        currentX = startX
        currentY = startY
        break
    }
  }
}

/**
 * Render SVG path directly to canvas (convenience function)
 */
export function renderSVGPath(
  ctx: CanvasRenderingContext2D,
  pathData: string,
  fill?: string,
  stroke?: string,
  strokeWidth: number = 1
): void {
  const commands = parseSVGPath(pathData)

  ctx.beginPath()
  executePathOnCanvas(ctx, commands)

  if (fill && fill !== 'none') {
    ctx.fillStyle = fill
    ctx.fill()
  }

  if (stroke && stroke !== 'none') {
    ctx.strokeStyle = stroke
    ctx.lineWidth = strokeWidth
    ctx.stroke()
  }
}
